import { riskCodeLabel } from "./risk-codes.v1";

// TES-1605 — moved here from apps/admin/src/lib so the api's work queue
// titles read the same labels as admin; the strings are unchanged.
//
// TES-538 (Ö-5) — the doctor never reads a rule id. Every
// flag and hard stop the evaluators raise (weekly-checkin-code.v4,
// intake-code.v2) has a Swedish, clinically plain label here; an id this map
// does not know renders as a neutral "Flagga" with the id in the tooltip,
// never as bare code.
//
// ⚠️ NOT CLINICALLY APPROVED. Emelie 2026-09-19 (Ö-183): the wordings are in
// the canonical 09C document and it states they match the code — but they are
// to be read as "implementerade förslag, inte kliniskt godkänd kanonisk copy"
// until Kirill has reviewed the list rule by rule. She will not approve every
// flag and hard stop as a batch without that.
//
// What the review is checking, in her words: that words like "Akut" and the
// diagnosis/risk signals do not promise
// "en annan klinisk hantering än den som faktiskt finns"
// The review sheet — rule id, label, what actually triggers
// it, and the priority/action it produces — is generated from this file plus
// the two evaluators: `docs/flag-rule-review.md`
// (`node scripts/generate-flag-rule-review.mjs`).
//
// DO NOT change these strings while the review is open. They are live on QA
// and the review is against what runs; editing them first would make the
// review describe something nobody is looking at. Replace verbatim when
// Kirill's approved wording lands (TES-810).

const FLAG_RULE_LABELS: Readonly<Record<string, string>> = {
  // --- Veckans check-in (weekly-checkin-code.v4) ---------------------------
  week_urgent_kraftig_buksmarta: "Akut: kraftig eller ihållande buksmärta",
  week_urgent_kan_inte_behalla:
    "Akut: kan inte behålla mat eller vätska (mer än ett dygn)",
  week_urgent_svimning: "Akut: har svimmat",
  week_urgent_blod: "Akut: blod i kräkning eller avföring",
  week_cannot_keep_intake: "Akut: svårt att behålla mat och vätska",
  week_symptoms_reported: "Nya eller förändrade symtom",
  week_severe_symptom: "Kraftiga symtom",
  week_adherence: "Har inte tagit dosen som planerat",
  week_low_wellbeing: "Lågt välmående (2 eller lägre av 5)",
  week_drive_mood_change: "Förändrad drivkraft eller sinnesstämning",
  week_drive_mild: "Lätt nedsatt drivkraft (hanterbart)",
  week_medical_question: "Vill ställa en medicinsk fråga",

  // --- The health declaration F-02 (intake-code.v2) -------------------------
  med_self_harm_risk: "Akut: uttryckt risk för självskada",
  med_self_harm_unanswered: "Ville inte svara på frågan om självskada",
  med_pregnancy_review: "Graviditet, amning eller graviditetsönskan",
  med_current_disordered_eating: "Pågående ätstörda beteenden",
  med_mtc_men2_family: "Medullär tyreoideacancer eller MEN2 i familjen",
  // TES-1164 — intake v17, asked only when F-01 did not ask it.
  med_type1_diabetes: "Diabetes typ 1 (ja eller osäker)",
  med_upcoming_procedure: "Planerat ingrepp eller operation",
  med_current_symptoms: "Aktuella besvär just nu",
  med_pancreatitis_history: "Tidigare bukspottkörtelinflammation",
  med_gallbladder_history: "Gallbesvär",
  med_kidney_history: "Njursjukdom",
  med_liver_history: "Leversjukdom",
  med_gi_history: "Mag-tarmsjukdom",
  med_gi_surgery: "Tidigare mag-tarmkirurgi",
  med_allergies: "Läkemedelsallergi",
  med_eating_disorder_history: "Tidigare ätstörning",
  med_mental_health_history: "Psykisk ohälsa",
  med_weight_medicine_history: "Tidigare viktläkemedel",
  med_recent_acute_care: "Nyligen sökt akut vård",
  med_conditions_unknown: "Osäker på egna diagnoser",
  med_bmi_27_30_no_current_comorbidity:
    "BMI 27–30 utan aktuell viktrelaterad samsjuklighet",
};

// TES-1001 part 1 — a flag from the versioned risk-code list is labelled by
// the list itself (risk-codes.v1.ts), not by this map, so this review sheet
// keeps describing only the evaluators' rules.
export function flagRuleLabel(rule: string): string {
  return FLAG_RULE_LABELS[rule] ?? riskCodeLabel(rule) ?? "Flagga";
}

/** True when the id has a real label; the caller shows the id as a tooltip otherwise. */
export function hasFlagRuleLabel(rule: string): boolean {
  return rule in FLAG_RULE_LABELS || riskCodeLabel(rule) !== undefined;
}

/**
 * TES-1605 — the label when there is one, else undefined (no "Flagga"
 * fallback), for a caller that says itself what it shows without one.
 */
export function flagRuleLabelOrUndefined(rule: string): string | undefined {
  return FLAG_RULE_LABELS[rule] ?? riskCodeLabel(rule);
}
